import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, ArrowLeft, Loader2, X } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { sendResetLink } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { isDemoMode } from '@/lib/demo/store';
import { MicrosoftSignInButton } from '@/components/auth/MicrosoftSignInButton';
import {
  getRememberedAccounts, restoreRememberedAccount, forgetAccount,
  rememberAccount, accountFromSession, isQuickLoginEnabled, type RememberedAccount,
} from '@/lib/quick-login';

interface TeacherLoginProps {
  onLogin: (teacherName: string, email: string, department: string, hasPassword: boolean, accessToken?: string, userId?: string, role?: string, schoolId?: string) => void;
  onBack: () => void;
  /** Trusted-device quick login: shows remembered-account cards. Opt-in is
   *  offered only at logout (see CMCApp). Defaults on; the onboarding wizard's
   *  teacher/cover mounts pass false. */
  enableQuickLogin?: boolean;
}

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';

export function TeacherLogin({ onLogin, onBack, enableQuickLogin = true }: TeacherLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Trusted-device quick login (remembered-account chooser)
  const [accounts, setAccounts] = useState<RememberedAccount[]>(() => enableQuickLogin ? getRememberedAccounts('teacher') : []);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [showAllForm, setShowAllForm] = useState(false);
  const hasChooser = enableQuickLogin && accounts.length > 0 && !showAllForm;

  // Forgot password flow
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotCooldown, setForgotCooldown] = useState(0);

  useEffect(() => {
    if (forgotCooldown <= 0) return;
    const t = setTimeout(() => setForgotCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [forgotCooldown]);

  const finishWithSession = (session: Session) => {
    const user = session.user;
    const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
    const metaName = (meta.full_name as string | undefined)
      ?? (meta.coverTeacherName as string | undefined)
      ?? `${meta.firstName ?? ''} ${meta.lastName ?? ''}`.trim();
    const name = metaName || (user.email ?? '').split('@')[0];
    const department = (meta.department as string | undefined) ?? '';
    onLogin(name, user.email ?? '', department, true, session.access_token, user.id, meta.role as string | undefined, meta.schoolId as string | undefined);
  };

  // Tap a remembered account → restore its session with one tap (no password).
  const pickAccount = async (acct: RememberedAccount) => {
    setRestoringId(acct.userId);
    setError('');
    const session = await restoreRememberedAccount(acct);
    if (!session) {
      setAccounts(getRememberedAccounts('teacher'));
      setRestoringId(null);
      setShowAllForm(true);
      setError('That saved sign-in has expired. Please sign in again.');
      return;
    }
    finishWithSession(session);
  };

  const removeAccount = (userId: string) => {
    forgetAccount(userId);
    const next = getRememberedAccounts('teacher');
    setAccounts(next);
    if (next.length === 0) setShowAllForm(true);
  };

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

      if (authError || !data.session) {
        setError('Invalid email or password. If you haven\'t set a password yet, use the link in your invitation email.');
        setIsValidating(false);
        return;
      }

      const session = data.session;
      const role = (session.user.user_metadata?.role as string | undefined);

      // Trusted-device quick login (teachers only): when the feature is already
      // on, remember this account so it shows as a one-tap card next time. The
      // opt-in itself is offered only at logout, never here.
      if (enableQuickLogin && role === 'teacher' && !isDemoMode() && isQuickLoginEnabled()) {
        const acct = accountFromSession(session, 'password');
        if (acct) rememberAccount(acct);
      }

      finishWithSession(session);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleForgotPassword = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      await sendResetLink(forgotEmail);
      setForgotSent(true);
      setForgotCooldown(60);
    } catch (err: unknown) {
      setForgotError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="glass-card p-8 md:p-10">
            <button
              onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); setForgotError(''); }}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm"
            >
              <ArrowLeft size={18} />
              Back to Login
            </button>

            {forgotSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-success" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Check Your Email</h2>
                <p className="text-muted-foreground mb-4">
                  We've sent a reset link to <strong>{forgotEmail}</strong>.<br />
                  <span className="text-sm">Check your spam folder if it doesn't arrive.</span>
                </p>
                {forgotCooldown > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Didn't receive it? Resend in <strong>{forgotCooldown}s</strong>
                  </p>
                ) : (
                  <button
                    onClick={() => handleForgotPassword()}
                    disabled={forgotLoading}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {forgotLoading ? 'Sending...' : 'Resend Link'}
                  </button>
                )}
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Reset Password</h1>
                <p className="text-muted-foreground mb-6">
                  Enter your school email and we'll send you a reset link.
                </p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="teacher-forgot-email" className="block text-sm font-semibold text-foreground mb-1">Email Address</label>
                    <input
                      id="teacher-forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={e => { setForgotEmail(e.target.value); setForgotError(''); }}
                      placeholder="teacher@school.edu"
                      className="input-premium"
                      required
                      autoFocus
                      autoComplete="email"
                    />
                  </div>
                  {forgotError && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                      <span className="text-sm text-destructive font-medium">{forgotError}</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8 md:p-10">
          {hasChooser ? (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">Welcome back</h1>
                <p className="text-muted-foreground text-sm">Tap your account to sign in</p>
              </div>

              <div className="space-y-2.5">
                {accounts.map((acct) => (
                  <div key={acct.userId} className="relative">
                    <button
                      type="button"
                      onClick={() => pickAccount(acct)}
                      disabled={restoringId !== null}
                      className="w-full flex items-center gap-3 p-3 pr-10 rounded-xl border border-border bg-background/60 hover:border-primary/50 hover:bg-primary/5 transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {initialsOf(acct.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-foreground truncate">{acct.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{acct.email}</div>
                      </div>
                      {restoringId === acct.userId && (
                        <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label={`Forget ${acct.name}`}
                      onClick={() => removeAccount(acct.userId)}
                      disabled={restoringId !== null}
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-2 mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                  >
                    <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                    <span className="text-sm text-destructive font-medium">{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="button"
                onClick={() => { setShowAllForm(true); setError(''); }}
                className="w-full mt-4 py-2.5 text-sm text-primary hover:underline"
              >
                Use a different account
              </button>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Teacher Login
                </h1>
                <p className="text-muted-foreground">
                  Sign in with your email &amp; password
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="teacher-login-email" className="block text-sm font-semibold text-foreground">
                    Email Address
                  </label>
                  <input
                    id="teacher-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="teacher@school.edu"
                    className="input-premium"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="teacher-login-password" className="block text-sm font-semibold text-foreground">
                    Password
                  </label>
                  <input
                    id="teacher-login-password"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="Enter your password"
                    className="input-premium"
                    autoComplete="current-password"
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  />
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setForgotEmail(email); }}
                    className="text-sm text-primary hover:underline px-1 py-2 -my-1"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-2 mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                  >
                    <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                    <span className="text-sm text-destructive font-medium">{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={handleSubmit}
                disabled={isValidating}
                className="btn-primary w-full mt-6 disabled:opacity-50"
              >
                {isValidating ? 'Signing in...' : 'Sign In'}
              </button>

              {/* Microsoft SSO — alternative sign-in for invited teachers */}
              <p className="text-center text-xs uppercase tracking-wide text-muted-foreground my-5">or</p>
              <MicrosoftSignInButton intent="teacher" />

              <p className="text-center text-xs text-muted-foreground mt-6">
                Haven't set up your password yet?{' '}
                <span className="text-primary">Check your invitation email for the setup link.</span>
              </p>
            </>
          )}

          <button
            onClick={onBack}
            className="w-full mt-3 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back
          </button>
        </div>
      </motion.div>
    </div>
  );
}
